import type { DayPickerLocale } from 'react-day-picker';
import { enUS } from 'react-day-picker/locale/en-US';
import { es } from 'react-day-picker/locale/es';
import { nb } from 'react-day-picker/locale/nb';

import { DISPLAY_DAY_FORMAT, parseDate, toDateString } from '@/utils/date';
import type { Locale } from '@/utils/localeFormatters';

/** The calendar's month names, weekdays and screen-reader labels per app language. */
export const PICKER_LOCALES: Record<Locale, DayPickerLocale> = { en: enUS, nb, es };

/**
 * Other ways of writing a day the input accepts: the dashed day it showed before, and
 * Norwegian's dotted short day. US month-first days are not read: the portal writes days
 * day-first in every language, and "09/10/2026" cannot mean both 9 October and 10 September.
 */
const OTHER_DAY_FORMATS: Record<Locale, string[]> = {
    en: ['dd-MM-yyyy'],
    nb: ['dd-MM-yyyy', 'dd.MM.yyyy'],
    es: ['dd-MM-yyyy'],
};

/** The rules that block a day from being picked or typed. */
export interface DayBounds {
    minDate: Date | null;
    maxDate: Date | null;
    blockFutureDates: boolean;
    useOutsideRange: boolean;
}

/** `date` at local noon, the time the picker hands days over at whichever way they come. */
export function atNoon(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
}

/**
 * The day typed into the input, or null. The input shows `dd/MM/yyyy`, and also takes the
 * formats above and `yyyy-MM-dd`; all are strict, so a partly typed day is no day.
 */
export function parseTypedDay(text: string, locale: Locale): Date | null {
    for (const format of [DISPLAY_DAY_FORMAT, ...OTHER_DAY_FORMATS[locale], 'yyyy-MM-dd']) {
        const day = parseDate(text, format);
        if (day) return atNoon(day);
    }
    return null;
}

/**
 * Whether a day may not be picked. Explicit bounds come first: `minDate` and `maxDate` say
 * "this cannot happen before/after that", while `blockFutureDates` and `useOutsideRange`
 * are general flags; `useOutsideRange` blocks today and every day before it.
 */
export function isDayBlocked(day: Date, bounds: DayBounds, today: Date = new Date()): boolean {
    const dayKey = toDateString(day);
    const todayKey = toDateString(today);

    if (bounds.minDate && dayKey < toDateString(bounds.minDate)) return true;
    if (bounds.maxDate && dayKey > toDateString(bounds.maxDate)) return true;
    if (bounds.blockFutureDates) return dayKey > todayKey;
    if (bounds.useOutsideRange) return dayKey <= todayKey;
    return false;
}
